/**
 * src/lib/apiFetch.ts
 *
 * `/api/*` 호출 공통 클라이언트.
 *
 * 존재 이유
 *   `res.json()` 을 status/Content-Type 확인 없이 호출하면, 서버가 SPA `index.html` 을
 *   반환했을 때 `Unexpected token '<'` 가 난다. 이번 Vercel -> Railway 이전의 원인이
 *   정확히 이것이며, 라우팅 회귀가 재발했을 때 즉시 식별되도록 전용 에러로 구분한다.
 *
 * 사용
 *   const data = await apiFetch<Project[]>('/api/sales/projects');
 *   await apiFetch('/api/sales/projects', { method: 'POST', json: { name: 'x' } });
 *   const res = await apiFetchRaw('/api/storage/download?id=1');   // 바이너리·리다이렉트
 */

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** 객체를 JSON 본문으로 보낸다. Content-Type 이 자동 설정된다. `body` 와 함께 쓰지 않는다. */
  json?: unknown;
  /** 원본 `fetch` 의 body. `json` 을 쓰지 않을 때만 사용한다. */
  body?: BodyInit | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: unknown;

  constructor(message: string, status: number, url: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

/**
 * `/api/*` 가 JSON 대신 HTML 을 반환했을 때 던진다.
 * 이 에러가 보이면 애플리케이션 버그가 아니라 **서버 라우팅 설정 문제**다.
 * (API 미들웨어보다 SPA fallback 이 앞에 있거나, 해당 경로의 rewrite 가 없음)
 */
export class ApiHtmlResponseError extends ApiError {
  constructor(status: number, url: string, snippet: string) {
    super(
      msg(
        `API 가 JSON 대신 HTML 을 반환했습니다 (${url}). ` +
          `서버 라우팅 설정을 확인하세요 — /api/* 가 SPA fallback 으로 처리되고 있을 수 있습니다.`,
        `The API returned HTML instead of JSON (${url}). ` +
          `Check the server routing — /api/* may be handled by the SPA fallback.`,
      ),
      status,
      url,
      snippet,
    );
    this.name = 'ApiHtmlResponseError';
  }
}

const HTML_SNIPPET_LENGTH = 200;

/**
 * 사용자에게 보일 수 있는 기본 메시지의 언어 — LanguageProvider 가 <html data-lang> 에 적어 둔 값을 읽는다.
 * (훅을 못 쓰는 모듈이라 DOM 에서 읽는다. 없으면 ko — 기존 문구 그대로.)
 */
function isEnglishUi(): boolean {
  try {
    return typeof document !== 'undefined' && document.documentElement.dataset.lang === 'en';
  } catch {
    return false;
  }
}
const msg = (ko: string, en: string) => (isEnglishUi() ? en : ko);

function isJsonContentType(contentType: string | null): boolean {
  return (contentType ?? '').toLowerCase().includes('application/json');
}

function looksLikeHtml(contentType: string | null, text: string): boolean {
  if ((contentType ?? '').toLowerCase().includes('text/html')) return true;
  return text.trimStart().startsWith('<');
}

function buildInit(init: ApiFetchInit): RequestInit {
  const { json, headers, ...rest } = init;
  const merged = new Headers(headers);
  let body = init.body ?? null;

  if (json !== undefined) {
    if (body !== null) {
      throw new Error('apiFetch: json 과 body 를 동시에 지정할 수 없습니다.');
    }
    body = JSON.stringify(json);
    if (!merged.has('Content-Type')) merged.set('Content-Type', 'application/json');
  }

  return {
    ...rest,
    headers: merged,
    body,
    // 기존 호출부가 쿠키 인증에 의존한다. 기본값을 유지한다.
    credentials: init.credentials ?? 'include',
  };
}

/**
 * 응답을 가공하지 않고 그대로 돌려준다.
 * 바이너리, 리다이렉트, 파일 다운로드 등 JSON 이 아닌 응답에 사용한다.
 * status/Content-Type 검사를 하지 않으므로 호출부가 직접 처리해야 한다.
 */
export async function apiFetchRaw(path: string, init: ApiFetchInit = {}): Promise<Response> {
  return fetch(path, buildInit(init));
}

/**
 * `/api/*` 를 호출하고 JSON 본문을 반환한다.
 *
 * 던지는 경우
 *   ApiHtmlResponseError  응답이 HTML 이다 (라우팅 회귀)
 *   ApiError              status 가 실패이거나, Content-Type 이 JSON 이 아니거나, JSON 파싱 실패
 *
 * 204 / 빈 본문은 `undefined` 를 반환한다.
 */
export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const res = await fetch(path, buildInit(init));
  const contentType = res.headers.get('content-type');
  const text = await res.text();

  if (looksLikeHtml(contentType, text)) {
    const snippet = text.slice(0, HTML_SNIPPET_LENGTH).replace(/\s+/g, ' ');
    // 라우팅 회귀를 즉시 식별할 수 있도록 원문 앞부분을 남긴다.
    console.error(`[apiFetch] ${path} 가 HTML 을 반환했습니다 (status ${res.status}):`, snippet);
    throw new ApiHtmlResponseError(res.status, path, snippet);
  }

  if (res.status === 204 || text.length === 0) {
    if (!res.ok) throw new ApiError(msg(`요청 실패 (${res.status})`, `Request failed (${res.status})`), res.status, path, null);
    return undefined as T;
  }

  if (!isJsonContentType(contentType)) {
    throw new ApiError(
      msg(`예상치 못한 Content-Type: ${contentType ?? '(없음)'} (${path})`, `Unexpected Content-Type: ${contentType ?? '(none)'} (${path})`),
      res.status,
      path,
      text.slice(0, HTML_SNIPPET_LENGTH),
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ApiError(
      msg(`JSON 파싱 실패 (${path})`, `Failed to parse JSON (${path})`),
      res.status,
      path,
      text.slice(0, HTML_SNIPPET_LENGTH),
    );
  }

  if (!res.ok) {
    const message =
      (parsed as { error?: string; message?: string } | null)?.error ??
      (parsed as { error?: string; message?: string } | null)?.message ??
      msg(`요청 실패 (${res.status})`, `Request failed (${res.status})`);
    throw new ApiError(message, res.status, path, parsed);
  }

  return parsed as T;
}

/**
 * 실패해도 예외를 던지지 않고 기본값을 돌려준다.
 * 기존 `.json().catch(() => ({}))` 패턴을 옮길 때 사용한다.
 * 다만 HTML 응답(라우팅 회귀)은 조용히 삼키면 안 되므로 콘솔 경고는 남는다.
 */
export async function apiFetchOr<T>(path: string, fallback: T, init: ApiFetchInit = {}): Promise<T> {
  try {
    const value = await apiFetch<T>(path, init);
    return value === undefined ? fallback : value;
  } catch (err) {
    if (err instanceof ApiHtmlResponseError) throw err;
    return fallback;
  }
}

/**
 * 이미 받은 Response 를 `apiFetch` 와 동일한 규칙으로 해석한다.
 *
 * 호출부에서 `fetch` 를 직접 수행하는 기존 응답 리더 래퍼
 * (`readPayload`, `toJson`, `readJson` 등)를 옮길 때 사용한다.
 * `apiFetch` 는 스스로 `fetch` 를 하므로 그런 래퍼 안에서는 부를 자리가 없다.
 *
 * 판정 규칙은 `apiFetch` 와 같다 — HTML 이면 `ApiHtmlResponseError`,
 * 그 외 실패는 `ApiError`, 204/빈 본문은 `undefined`.
 */
export async function readApiResponse<T = unknown>(res: Response): Promise<T> {
  const url = res.url || '(unknown)';
  const contentType = res.headers.get('content-type');
  const text = await res.text();

  if (looksLikeHtml(contentType, text)) {
    const snippet = text.slice(0, HTML_SNIPPET_LENGTH).replace(/\s+/g, ' ');
    console.error(`[apiFetch] ${url} 가 HTML 을 반환했습니다 (status ${res.status}):`, snippet);
    throw new ApiHtmlResponseError(res.status, url, snippet);
  }

  if (res.status === 204 || text.length === 0) {
    if (!res.ok) throw new ApiError(msg(`요청 실패 (${res.status})`, `Request failed (${res.status})`), res.status, url, null);
    return undefined as T;
  }

  if (!isJsonContentType(contentType)) {
    throw new ApiError(
      msg(`예상치 못한 Content-Type: ${contentType ?? '(없음)'} (${url})`, `Unexpected Content-Type: ${contentType ?? '(none)'} (${url})`),
      res.status,
      url,
      text.slice(0, HTML_SNIPPET_LENGTH),
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ApiError(msg(`JSON 파싱 실패 (${url})`, `Failed to parse JSON (${url})`), res.status, url, text.slice(0, HTML_SNIPPET_LENGTH));
  }

  if (!res.ok) {
    const payload = parsed as { error?: string; message?: string } | null;
    throw new ApiError(
      payload?.error ?? payload?.message ?? msg(`요청 실패 (${res.status})`, `Request failed (${res.status})`),
      res.status,
      url,
      parsed,
    );
  }

  return parsed as T;
}
